import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useAuth } from '../context/AuthContext';
import { authApi, perfilApi } from '../lib/api';
import { Toast, type ToastState } from '../components/Toast';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => { const r = reader.result as string; resolve(r.split(',')[1]); };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function Avatar({ url, nombre, size = 92 }: { url: string | null; nombre: string; size?: number }) {
  if (url) {
    return (
      <img src={url} alt={nombre} style={{
        width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
        border: '1px solid var(--border)',
      }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(140deg, var(--copper), var(--sage))',
      display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 700,
      fontSize: size * 0.36, fontFamily: 'var(--serif)',
    }}>
      {(nombre || 'U')[0]?.toUpperCase()}
    </div>
  );
}

export default function Perfil() {
  const { user, updateUser } = useAuth();
  const [toast, setToast] = useState<ToastState | null>(null);

  const [loading, setLoading]   = useState(true);
  const [nombre, setNombre]     = useState('');
  const [email, setEmail]       = useState('');
  const [fotoUrl, setFotoUrl]   = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving]     = useState(false);

  const [pwActual, setPwActual]   = useState('');
  const [pwNueva, setPwNueva]     = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving]   = useState(false);

  const showToast = (message: string, type: ToastState['type']) => setToast({ message, type });

  useEffect(() => {
    authApi.me()
      .then((r) => {
        setNombre(r.user.nombre ?? '');
        setEmail(r.user.email ?? '');
        setFotoUrl(r.user.foto_url ?? r.user.avatar_url ?? null);
        updateUser(r.user);
      })
      .catch(() => {
        setNombre(user?.nombre ?? '');
        setEmail(user?.email ?? '');
        setFotoUrl(user?.foto_url ?? user?.avatar_url ?? null);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFotoChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const input = e.target;
    if (!file) return;

    if (file.size > 4 * 1024 * 1024) {
      showToast('La foto no puede pesar más de 4 MB', 'error');
      input.value = '';
      return;
    }

    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const r = await perfilApi.subirFoto({ foto_base64: base64, mime_type: file.type });
      setFotoUrl(r.foto_url);
      updateUser({ foto_url: r.foto_url });
      authApi.me().then((me) => updateUser(me.user)).catch(() => {});
      showToast('Foto actualizada', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo subir la foto', 'error');
    } finally {
      setUploading(false);
      input.value = '';
    }
  }

  async function handleSavePerfil(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await perfilApi.updatePerfil({ nombre: nombre.trim() });
      updateUser({ nombre: nombre.trim() });
      authApi.me().then((me) => updateUser(me.user)).catch(() => {});
      showToast('Perfil actualizado', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo guardar', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleCambiarPassword(e: FormEvent) {
    e.preventDefault();
    if (pwNueva.length < 8) {
      showToast('La nueva contraseña debe tener al menos 8 caracteres', 'error');
      return;
    }
    if (pwNueva !== pwConfirm) {
      showToast('Las contraseñas no coinciden', 'error');
      return;
    }
    setPwSaving(true);
    try {
      await perfilApi.cambiarPassword({ password_actual: pwActual, password_nueva: pwNueva });
      setPwActual(''); setPwNueva(''); setPwConfirm('');
      showToast('Contraseña actualizada', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo cambiar la contraseña', 'error');
    } finally {
      setPwSaving(false);
    }
  }

  if (loading) {
    return <div className="loading-center"><span className="spinner" /> Cargando perfil…</div>;
  }

  return (
    <div className="page">
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontFamily: 'var(--serif)', fontSize: 28, margin: 0 }}>Mi perfil</h1>
        <p className="muted" style={{ margin: '7px 0 0', fontSize: 14 }}>
          Administra tu foto, tu nombre y tu contraseña de acceso al panel.
        </p>
      </div>

      <div className="card fade-up" style={{ marginBottom: 'var(--gap)' }}>
        <div className="card-pad">
          <div className="row" style={{ gap: 20, marginBottom: 24, flexWrap: 'wrap' }}>
            <Avatar url={fotoUrl} nombre={nombre} />
            <div>
              <label className="btn btn-ghost btn-sm" style={{ cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
                {uploading ? <span className="spinner spinner--sm" /> : <CameraIco size={15} />}
                {uploading ? 'Subiendo…' : 'Cambiar foto'}
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={handleFotoChange} disabled={uploading} />
              </label>
              <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>JPG o PNG, máx. 4 MB</div>
            </div>
          </div>

          <form onSubmit={handleSavePerfil}>
            <label className="field-label">Nombre</label>
            <input className="input" style={{ marginBottom: 16 }}
              value={nombre} onChange={(e) => setNombre(e.target.value)}
              placeholder="Tu nombre" maxLength={100} />

            <label className="field-label">Correo</label>
            <input className="input" value={email} readOnly
              style={{ marginBottom: 20, opacity: 0.6, cursor: 'not-allowed' }} />

            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner spinner--sm" /> : <CheckIco size={15} />}
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </form>
        </div>
      </div>

      <div className="card fade-up">
        <div className="card-pad">
          <div style={{ fontFamily: 'var(--serif)', fontSize: 17, marginBottom: 18 }}>Cambiar contraseña</div>
          <form onSubmit={handleCambiarPassword}>
            <label className="field-label">Contraseña actual</label>
            <input type="password" className="input" style={{ marginBottom: 16 }}
              value={pwActual} onChange={(e) => setPwActual(e.target.value)}
              placeholder="••••••••" />

            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: 20 }}>
              <div>
                <label className="field-label">Nueva contraseña</label>
                <input type="password" className="input"
                  value={pwNueva} onChange={(e) => setPwNueva(e.target.value)}
                  placeholder="Mínimo 8 caracteres" />
              </div>
              <div>
                <label className="field-label">Confirmar</label>
                <input type="password" className="input"
                  value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)}
                  placeholder="Repite la contraseña" />
              </div>
            </div>

            <button type="submit" className="btn btn-ghost" disabled={pwSaving}>
              {pwSaving ? <span className="spinner spinner--sm" /> : <LockIco size={15} />}
              {pwSaving ? 'Cambiando…' : 'Cambiar contraseña'}
            </button>
          </form>
        </div>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

/* ── Icons ── */
function Svg({ children, size = 18 }: { children: React.ReactNode; size?: number }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
}
function CameraIco({ size = 18 }: { size?: number }) { return <Svg size={size}><path d="M4 8a2 2 0 0 1 2-2h1l1.5-2h7L17 6h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/><circle cx="12" cy="13" r="3.5"/></Svg>; }
function CheckIco({ size = 18 }: { size?: number })  { return <Svg size={size}><path d="M5 12.5l4.5 4.5L19 6.5"/></Svg>; }
function LockIco({ size = 18 }: { size?: number })   { return <Svg size={size}><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></Svg>; }
